import { UniqueIdentifier } from "@dnd-kit/core";
import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import WARRANTIES_ICON from "@redotech/redo-icons/shield-tick.svg";
import WARRANTIES_ICON_FILLED from "@redotech/redo-icons/shield-tick_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { WarrantySystemView } from "@redotech/redo-return-operations-common/views/default-return-views";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { recordListHref } from "../../../../return/record-links/record-links";
import { WarrantyViewOrderingContext } from "../../../../services/warranties/warranty-view-ordering-service";
import { nameReturnsNavTree } from "../returns/returns-nav-action-names";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const WarrantiesItem = memo(function WarrantiesItem() {
  const { t, i18n } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const warrantiesEnabled = useCapability("warranties");
  const warrantyRegistrationEnabled =
    !!team.settings.warranties?.registrationEnabled;

  const hasPermissionToViewItem = useHasPermission(
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
  );

  const warrantyViewOrderingContext = useRequiredContext(
    WarrantyViewOrderingContext,
  );

  const allViews = useMemo(() => {
    return [...warrantyViewOrderingContext.views].filter(
      (view) => view.name !== WarrantySystemView.CREATE,
    );
  }, [warrantyViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...warrantyViewOrderingContext.folders];
  }, [warrantyViewOrderingContext]);

  const notificationCounts = warrantyViewOrderingContext.notificationCounts;

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"warranties">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const sortableItems = useMemo(() => {
    void i18n.language;
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix: routes.warrantiesHref,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    routes.warrantiesHref,
    notificationCounts,
    folderNotificationCounts,
    i18n.language,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    warrantyViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"warranties">(
      flattenedItems,
      allViews,
      allFolders,
    );
    warrantyViewOrderingContext.updateOrdering(views, folders);
  });

  const namedSortableItems = useMemo(
    () => nameReturnsNavTree(sortableItems, "warranties"),
    [sortableItems],
  );

  const registrationLink = routes.registrationsHref;

  const warrantiesMatch = useMatch(
    `${routes.getWarrantiesHref({ enabled: true })}/*`,
  );
  const registrationsMatch = useMatch(`${registrationLink}/*`);
  const anyChildSelected = warrantiesMatch || registrationsMatch;

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getWarrantiesHref({ enabled: warrantiesEnabled }),
      }}
      anyChildSelected={!!anyChildSelected}
      data-dd-action-name="returns_nav:warranties_link_click"
      FilledIcon={WARRANTIES_ICON_FILLED}
      friendlyName={t("Warranties", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="warranties"
      notificationCount={totalNotificationCount}
      UnfilledIcon={WARRANTIES_ICON}
    >
      <AllWarrantiesItem hidden={!warrantiesEnabled} />
      <WarrantyRegistrationsItem
        hidden={!warrantyRegistrationEnabled}
        href={registrationLink}
      />
      {warrantiesEnabled && (
        <SortableTree
          items={namedSortableItems}
          onRequestEdit={handleRequestEdit}
          setItems={handleUpdateOrdering}
        />
      )}
      <WarrantiesAddViewItem hidden={!warrantiesEnabled} />
      <WarrantiesCreateFolderItem hidden={!warrantiesEnabled} />
    </RedoSecondaryNavItem>
  );
});

const AllWarrantiesItem = memo(function AllWarrantiesItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: recordListHref(routes.returnsBaseUrl, ReturnTypeEnum.WARRANTY),
      }}
      data-dd-action-name="returns_nav:all_warranties_link_click"
      friendlyName={t("All warranties", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      hidden={hidden}
      id="all-warranties"
    />
  );
});

const WarrantyRegistrationsItem = memo(function WarrantyRegistrationsItem({
  href,
  hidden,
}: {
  href: string;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      data-dd-action-name="returns_nav:warranty_registrations_link_click"
      friendlyName={t("Registrations", {
        context: "warranty navigation label for product warranty registrations",
      })}
      hidden={hidden}
      id="registrations"
    />
  );
});

const WarrantiesAddViewItem = memo(function WarrantiesAddViewItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getWarrantiesSubRoute("create") }}
      data-dd-action-name="returns_nav:add_warranty_view_link_click"
      friendlyName={t("+ Add new view", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      hidden={hidden}
      id="orders-warranties-create"
    />
  );
});

const WarrantiesCreateFolderItem = memo(function WarrantiesCreateFolderItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const warrantyViewOrderingContext = useRequiredContext(
    WarrantyViewOrderingContext,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          warrantyViewOrderingContext.openCreateFolderModal();
        },
      }}
      data-dd-action-name="returns_nav:add_warranty_folder_button_click"
      friendlyName={t("+ Add new folder", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      hidden={hidden}
      id="orders-warranties-create-folder"
    />
  );
});
